import React, { useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useToolStore } from '../store/useToolStore';
import { Loader } from './Loader';
import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, AlertCircle, Download } from 'lucide-react';

interface GalleryImage {
    name: string;
    url: string;
    created_at: string;
}

const Gallery: React.FC = () => {
    const { profile } = useAuth();
    const { setUploadedImage, setActiveTool } = useToolStore();
    const navigate = useNavigate();
    const [images, setImages] = useState<GalleryImage[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchImages = async () => {
            if (!profile?.id) return;

            try {
                setLoading(true);
                const { data, error } = await supabase.storage
                    .from('generated-images')
                    .list(profile.id, {
                        sortBy: { column: 'created_at', order: 'desc' }
                    });

                if (error) throw error;

                if (data) {
                    // Filter out any placeholders or folders if necessary
                    // And construct public URLs
                    const imageFiles = data.filter(f => f.name.endsWith('.png') || f.name.endsWith('.jpg') || f.name.endsWith('.jpeg'));

                    const imagesWithUrls = imageFiles.map(file => {
                        const { data: { publicUrl } } = supabase.storage
                            .from('generated-images')
                            .getPublicUrl(`${profile.id}/${file.name}`);

                        return {
                            name: file.name,
                            url: publicUrl,
                            created_at: file.created_at,
                        };
                    });

                    setImages(imagesWithUrls);
                }
            } catch (err) {
                console.error('Error fetching images:', err);
                setError('Failed to load gallery images.');
            } finally {
                setLoading(false);
            }
        };

        fetchImages();
    }, [profile?.id]);

    const handleImageClick = async (imageUrl: string) => {
        try {
            // Fetch the blob to convert to base64 for the editor
            const response = await fetch(imageUrl);
            const blob = await response.blob();
            const reader = new FileReader();

            reader.onloadend = () => {
                if (reader.result) {
                    setUploadedImage(reader.result as string);
                    setActiveTool('render-bridge'); // Default to a tool
                    navigate('/app/refiner'); // Navigate to an editor view
                }
            };
            reader.readAsDataURL(blob);
        } catch (err) {
            console.error('Error loading image to editor:', err);
            setError('Failed to load image into editor.');
        }
    };

    const handleDownload = async (e: React.MouseEvent, image: GalleryImage) => {
        e.stopPropagation(); // Prevent opening editor
        try {
            const response = await fetch(image.url);
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // Force PNG extension if not present, though most generated are PNG.
            // Using logic to respect default behavior but ensure name.
            link.download = image.name;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Error downloading image:', err);
            // Optionally set error state or alert
        }
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center h-full w-full bg-stone-950 pointer-events-auto">
                <Loader />
            </div>
        );
    }

    return (
        <div className="flex flex-col h-full w-full bg-stone-950 p-8 pointer-events-auto overflow-y-auto">
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-white flex items-center gap-2">
                    <ImageIcon className="w-6 h-6 text-indigo-500" />
                    Gallery
                </h2>
                <div className="flex items-center gap-2 text-sm text-yellow-500 bg-yellow-500/10 px-3 py-1.5 rounded-full border border-yellow-500/20">
                    <AlertCircle className="w-4 h-4" />
                    <span>System saves a max of 20 images</span>
                </div>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500/20 text-red-500 px-4 py-3 rounded-lg mb-6">
                    {error}
                </div>
            )}

            {images.length === 0 ? (
                <div className="flex flex-col items-center justify-center flex-1 text-zinc-500">
                    <ImageIcon className="w-16 h-16 mb-4 opacity-20" />
                    <p>No generated images found.</p>
                    <p className="text-sm">Generate some art to see it here!</p>
                </div>
            ) : (
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4 pb-20">
                    {images.map((image) => (
                        <div
                            key={image.name}
                            onClick={() => handleImageClick(image.url)}
                            className="group relative aspect-square rounded-xl overflow-hidden border border-zinc-800 bg-black cursor-pointer hover:border-indigo-500/50 transition-all hover:shadow-lg hover:shadow-indigo-500/10"
                        >
                            <img
                                src={`${image.url}?width=500&height=500&resize=cover`}
                                alt="Generated"
                                loading="lazy"
                                decoding="async"
                                className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                            />
                            <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
                                <span className="text-white font-medium px-4 py-2 bg-black/50 backdrop-blur rounded-full border border-white/10 text-sm transform translate-y-2 group-hover:translate-y-0 transition-transform hover:bg-black/70">
                                    Edit
                                </span>
                                <button
                                    onClick={(e) => handleDownload(e, image)}
                                    className="p-2 bg-black/50 backdrop-blur rounded-full border border-white/10 text-white transform translate-y-2 group-hover:translate-y-0 transition-transform hover:bg-black/70 hover:text-indigo-400"
                                    title="Download PNG"
                                >
                                    <Download size={18} />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default Gallery;
